/**
 * AvatarPayment — Stripe payment wrapper for avatar regeneration
 * IDENTITY:P2.D
 *
 * Creates PaymentIntent and renders PaymentElement for $2.99 payment.
 * Follows existing CheckoutForm.jsx pattern.
 */

import React, { useState, useEffect } from 'react';
import { createAvatarPaymentIntent } from '../../services/avatarApi.js';

export default function AvatarPayment({ onPaymentComplete, onCancel }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [amountCents, setAmountCents] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFree, setIsFree] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function initPayment() {
      try {
        const data = await createAvatarPaymentIntent();
        if (mounted) {
          setClientSecret(data.client_secret);
          setAmountCents(data.amount_cents);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          // 409 means generation is free
          if (err.status === 409 || (err.message && err.message.includes('free'))) {
            setIsFree(true);
            setLoading(false);
            onPaymentComplete?.(null);
            return;
          }
          setError(err.message || 'Failed to create payment');
          setLoading(false);
        }
      }
    }
    initPayment();
    return () => { mounted = false; };
  }, [onPaymentComplete]);

  if (isFree) {
    return (
      <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-700" role="status">
        <p className="text-green-400 text-sm">You have a free generation available!</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-700" role="status" aria-label="Loading payment">
        <p className="text-zinc-400 text-sm">Setting up payment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-zinc-900 rounded-lg border border-red-800" role="alert">
        <p className="text-red-400 text-sm">{error}</p>
        <button
          onClick={onCancel}
          className="mt-2 text-sm text-zinc-400 hover:text-white transition-colors
            focus:outline-none focus:ring-2 focus:ring-orange-500 rounded"
          aria-label="Cancel payment"
        >
          Cancel
        </button>
      </div>
    );
  }

  // Simple payment confirmation UI (Stripe Elements integration would go here)
  return (
    <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-700 space-y-3">
      <p className="text-sm text-zinc-300">
        Avatar regeneration: <span className="text-orange-400 font-medium">${(amountCents / 100).toFixed(2)}</span>
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => onPaymentComplete?.(clientSecret)}
          className="flex-1 py-2 px-4 rounded-lg bg-orange-500 text-white text-sm font-medium
            hover:bg-orange-600 transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
          aria-label="Confirm payment"
        >
          Pay & Generate
        </button>
        <button
          onClick={onCancel}
          className="py-2 px-4 rounded-lg border border-zinc-700 text-zinc-400 text-sm
            hover:text-white transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
          aria-label="Cancel payment"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
